// CANmail Constants
// Shared constants for CANmail functionality

/**
 * Maximum total attachment size limit in MB
 * This limit applies to the TOTAL size of all attachments in an email
 */
export const MAX_ATTACHMENT_SIZE_MB = 10;

/**
 * Maximum total attachment size limit in bytes
 */
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

/**
 * Minimum viewport width (px) at which the Unlayer "Drop" editor is usable.
 *
 * Unlayer has no mobile support — below this width the composer forces the
 * non-Drop editor and hides the editor toggle entirely.
 *
 * This MUST stay a single source of truth. It previously existed as two
 * disagreeing checks (600px in EmailSender, 1000px in DropEditor), which left a
 * dead zone at 600–999px where the toggle was shown but the Drop editor refused
 * to render.
 */
export const DROP_EDITOR_MIN_WIDTH = 1000;

/**
 * Media query matching viewports wide enough for the Drop editor.
 */
export const DROP_EDITOR_MEDIA_QUERY = `(min-width:${DROP_EDITOR_MIN_WIDTH}px)`;

/**
 * Email address validation used across the composer.
 *
 * Deliberately shared so every entry point accepts exactly the same set of addresses —
 * a Test Send must not reject something a real send would allow.
 *
 * Note this is intentionally *not* `libs/validations/validateEmail`, which enforces a
 * 10-character minimum (rejecting valid short addresses like `a@b.co`) and returns a
 * different shape.
 */
export const EMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Single address, or a comma-separated list of them. Used for the Reply-to field.
 */
export const MULTI_EMAIL_ADDRESS_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]+(\s*,\s*[^\s@]+@[^\s@]+\.[^\s@]+)*$/;

/**
 * Colours for the survey button injected into an email design.
 *
 * ## Why these are literals and NOT read from the MUI theme
 *
 * `UI_UX_STANDARDS.md` forbids hardcoded colours because they break dark mode — but
 * that rule is about **app chrome**, and these values are **email content**. They are
 * serialised into the Unlayer design JSON and rendered in the recipient's mail client,
 * which has no access to our theme.
 *
 * Reading `theme.palette.primary` at insert time would be actively wrong here:
 * - `primary.main` differs by mode (`#64b5f6` light vs `#90caf9` dark), so the same
 *   button would be emailed in different colours depending on whether the *author*
 *   had dark mode enabled.
 * - `contrastText` in dark mode is `rgba(0, 0, 0, 0.87)`, and rgba() is unreliable in
 *   several email clients.
 *
 * Centralised here so there is still a single source of truth. Change deliberately —
 * every email sent afterwards will use the new values.
 */
export const SURVEY_BUTTON_COLORS = {
  color: '#FFFFFF',
  backgroundColor: '#3AAEE0',
  hoverColor: '#FFFFFF',
  hoverBackgroundColor: '#2A8EC0',
};
